import { cn } from "@core-ui/utils/cn";
import t from "@ext/localization/locale/translate";
import getCarouselStyles from "@ext/markdown/elements/gallery/logic/getCarouselStyles";
import useCarouselPosition, { getCarouselItems } from "@ext/markdown/elements/gallery/render/hooks/useCarouselPosition";
import type { GalleryViewProps } from "@ext/markdown/elements/gallery/render/views/types";
import { IconButton } from "@ui-kit/Button";
import { useHorizontalScrollShadow } from "@ui-kit/ScrollShadowContainer";
import { useCallback, useRef } from "react";

interface CarouselArrowProps {
	side: "left" | "right";
	onClick: () => void;
}

const CarouselArrow = ({ side, onClick }: CarouselArrowProps) => {
	const label = t(side === "left" ? "editor.gallery.previous" : "editor.gallery.next");
	return (
		<IconButton
			aria-label={label}
			className={cn(
				"absolute top-1/2 z-10 -translate-y-1/2 rounded-full shadow-md",
				side === "left" ? "left-2" : "right-2",
			)}
			contentEditable={false}
			icon={side === "left" ? "chevron-left" : "chevron-right"}
			onClick={onClick}
			onMouseDown={(event) => event.preventDefault()}
			size="sm"
			variant="outline"
		/>
	);
};

const CarouselView = ({ scope, items, children }: GalleryViewProps) => {
	const stageRef = useRef<HTMLDivElement>(null);
	const { index, step } = useCarouselPosition(stageRef);
	// biome-ignore lint/correctness/useExhaustiveDependencies: slides added or removed change the scroll width
	const getItems = useCallback(() => getCarouselItems(stageRef.current), [items.length]);
	useHorizontalScrollShadow(getItems);

	const current = Math.min(index, items.length - 1);
	const hasMany = items.length > 1;

	return (
		<div className="relative" ref={stageRef}>
			<style>
				{getCarouselStyles(
					scope,
					items.map((item) => item.ratio),
				)}
			</style>
			{children}
			{hasMany && current > 0 && <CarouselArrow onClick={() => step(-1)} side="left" />}
			{hasMany && current < items.length - 1 && <CarouselArrow onClick={() => step(1)} side="right" />}
		</div>
	);
};

export default CarouselView;
